import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiErrors';
import { notifyAllocationConflicts } from '../utils/allocationConflicts';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { DatePicker } from '../components/ui/date-picker';
import { Separator } from '../components/ui/separator';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import {
  Building2,
  Truck,
  MapPin,
  ExternalLink,
  Loader2,
  Save,
  Moon,
  Navigation,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const STATUS_MAP = {
  not_allocated: { label: 'Not Allocated', bg: 'var(--status-not-allocated-bg)', text: 'var(--status-not-allocated-text)', border: 'var(--status-not-allocated-border)' },
  allocated: { label: 'Allocated', bg: 'var(--status-allocated-bg)', text: 'var(--status-allocated-text)', border: 'var(--status-allocated-border)' },
  confirmed: { label: 'Confirmed', bg: 'var(--status-confirmed-bg)', text: 'var(--status-confirmed-text)', border: 'var(--status-confirmed-border)' },
  docketed: { label: 'Docketed', bg: 'var(--status-docketed-bg)', text: 'var(--status-docketed-text)', border: 'var(--status-docketed-border)' },
  invoiced: { label: 'Invoiced', bg: 'var(--status-invoiced-bg)', text: 'var(--status-invoiced-text)', border: 'var(--status-invoiced-border)' },
  tbc: { label: 'TBC', bg: '#71717a20', text: '#a1a1aa', border: '#71717a' },
};

const getStatusBadge = (status) => {
  const s = STATUS_MAP[status];
  if (!s) return <Badge variant="secondary">{status}</Badge>;
  return (
    <Badge className="border" style={{ backgroundColor: s.bg, color: s.text, borderColor: s.border }}>
      {s.label}
    </Badge>
  );
};

export default function JobEditSheet({ jobId, open, onClose, onSaved, api, customers, sites, tenantSettings, currentDate, warnings = [] }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null);
  const [form, setForm] = useState({});

  const isLocked = job?.status === 'docketed' || job?.status === 'invoiced';

  // Fetch full job data when sheet opens
  const fetchJob = useCallback(async () => {
    if (!jobId || !open) return;
    try {
      setLoading(true);
      const res = await api.get(`/jobs/${jobId}`);
      const j = res.data;
      setJob(j);
      setForm({
        description: j.description || '',
        start_date: j.start_date || '',
        end_date: j.end_date || '',
        start_time: j.start_time || '07:00',
        end_time: j.end_time || '',
        depot_time: j.depot_time || '',
        est_duration: j.est_duration || '',
        notes: j.notes || '',
        po_number: j.po_number || '',
        status: j.status || 'not_allocated',
        customer_id: j.customer_id || '',
        site_id: j.site_id || '',
        equipment_type: j.equipment_type || '',
        contact_id: j.contact_id || '',
        site_contact_id: j.site_contact_id || '',
        one_off_street: j.one_off_street || '',
        one_off_suburb: j.one_off_suburb || '',
        one_off_state: j.one_off_state || '',
        one_off_postcode: j.one_off_postcode || '',
        starting_location: j.starting_location || '',
        tags: (j.tags || []).map(tag => typeof tag === 'string' ? tag : (tag?.name || String(tag))),
        worker_requirements: j.worker_requirements || [],
        attachment_type_ids: j.attachment_type_ids || [],
        excluded_dates: j.excluded_dates || [],
        // Dry-Hire fields — pass through so a partial save on this quick-edit
        // sheet doesn't reset hire_type / period back to defaults.
        hire_type: j.hire_type || 'wet',
        hire_period_unit: j.hire_period_unit || null,
        pickup_from_yard: !!j.pickup_from_yard,
        return_to_yard: !!j.return_to_yard,
        hire_notes: j.hire_notes || null,
        hire_status: j.hire_status || null,
        minimum_hire_days: j.minimum_hire_days ?? null,
        include_weekends_in_rate: j.include_weekends_in_rate ?? null,
        is_night_shift: !!j.is_night_shift,
        is_tbc: !!j.is_tbc,
        asset_requirements: j.asset_requirements || null,
      });
    } catch (err) {
      console.error('Failed to load job:', err);
      toast.error('Failed to load job details');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [jobId, open, api, onClose]);

  useEffect(() => {
    if (open && jobId) {
      fetchJob();
    } else {
      setJob(null);
      setForm({});
    }
  }, [open, jobId, fetchJob]);

  // Resolve display names
  const customerName = customers?.find(c => c.id === job?.customer_id)?.company_name || '-';
  const site = sites?.find(s => s.id === job?.site_id);
  const siteName = site ? (site.name || [site.street_address, site.suburb].filter(Boolean).join(', '))  + (site.suburb && site.name ? ` - ${site.suburb}` : '') : null;
  const locationDisplay = siteName || (job?.one_off_street ? `${job.one_off_street}, ${job.one_off_suburb || ''} ${job.one_off_state || ''}`.trim() : 'No Location');

  // Format date for tenant display
  const formatDateDisplay = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = typeof dateString === 'string'
      ? (dateString.includes('T') ? new Date(dateString) : new Date(dateString + 'T00:00:00'))
      : dateString;
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const fmt = tenantSettings?.date_format || 'DD/MM/YYYY';
    switch (fmt) {
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      default: return `${day}/${month}/${year}`;
    }
  }, [tenantSettings?.date_format]);

  // Calculated end time
  const calcEndTime = () => {
    if (!form.start_time || !form.est_duration) return null;
    const [h, m] = form.start_time.split(':').map(Number);
    const dH = Math.floor(form.est_duration);
    const dM = (form.est_duration % 1) * 60;
    let eH = h + dH;
    let eM = m + dM;
    if (eM >= 60) { eH++; eM -= 60; }
    const nextDay = eH >= 24;
    if (nextDay) eH -= 24;
    return { time: `${String(eH).padStart(2, '0')}:${String(Math.round(eM)).padStart(2, '0')}`, nextDay };
  };

  // Save handler
  const handleSave = async () => {
    if (isLocked) return;
    if (!form.description?.trim()) { toast.error('Description is required'); return; }
    if (!form.start_date) { toast.error('Start Date is required'); return; }
    if (!form.start_time) { toast.error('Start Time is required'); return; }
    const _slRule = tenantSettings?.field_rules?.starting_location;
    if (_slRule?.enabled && _slRule?.required && !form.starting_location?.trim()) { toast.error('Starting Location is required'); return; }

    try {
      setSaving(true);
      const submitData = {
        ...form,
        version: job.version,
        end_date: form.end_date || null,
        est_duration: form.est_duration || null,
        depot_time: form.depot_time || null,
      };
      // Clean use_one_off_address flag if present
      delete submitData.use_one_off_address;

      const res = await api.put(`/jobs/${jobId}`, submitData);
      toast.success(`Job #${job.job_number} updated`);
      notifyAllocationConflicts(res.data);
      onSaved?.();
      onClose();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('This job was modified by another user. Please close and reopen.');
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to update job'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFullEditor = () => {
    onClose();
    const dateStr = currentDate ? format(currentDate, 'yyyy-MM-dd') : '';
    navigate(`/schedule?editJob=${jobId}${dateStr ? `&date=${dateStr}` : ''}`);
  };

  const endTime = calcEndTime();

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="right"
        className="w-[520px] sm:max-w-[520px] flex flex-col"
        data-testid="job-edit-sheet"
      >
        {/* Header */}
        <SheetHeader className="pb-3 border-b shrink-0">
          <div className="flex items-center gap-3">
            <SheetTitle className="font-heading text-lg flex-1" data-testid="job-edit-sheet-title">
              {loading ? 'Loading...' : job ? (
                <>
                  {isLocked ? 'View' : 'Edit'} Job #{job.job_number}
                </>
              ) : 'Job Details'}
            </SheetTitle>
            {job && getStatusBadge(job.status)}
          </div>
          <SheetDescription className="sr-only">Edit job details</SheetDescription>
          {isLocked && (
            <p className="text-xs text-muted-foreground mt-1">
              This job is {job?.status} and cannot be modified.
            </p>
          )}
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : job ? (
            <>
              {/* Read-only summary section */}
              <div className="bg-muted/50 border border-border rounded-lg p-3 space-y-2" data-testid="job-summary-card">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground text-xs">Customer</span>
                    <p className="font-medium flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {customerName}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Asset Type</span>
                    {form.asset_requirements?.length > 0 ? (
                      <div className="space-y-0.5">
                        {form.asset_requirements.map((r, i) => (
                          <p key={i} className="font-medium flex items-center gap-1.5 text-sm">
                            <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                            {r.equipment_type}{r.quantity > 1 && <span className="text-muted-foreground text-xs">x{r.quantity}</span>}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="font-medium flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                        {job.equipment_type || '-'}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground text-xs">Job Location</span>
                    <p className="font-medium flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {locationDisplay}
                    </p>
                  </div>
                </div>
              </div>

              {/* Allocation Warnings */}
              {warnings.length > 0 && (
                <div className="border rounded-lg overflow-hidden" data-testid="job-edit-warnings">
                  <div className={`px-3 py-2 flex items-center gap-2 ${warnings.some(w => w.severity === 'error') ? 'bg-red-500/10 border-b border-red-500/20' : 'bg-amber-500/10 border-b border-amber-500/20'}`}>
                    <AlertTriangle className={`h-4 w-4 ${warnings.some(w => w.severity === 'error') ? 'text-red-400' : 'text-amber-400'}`} />
                    <span className={`text-xs font-semibold ${warnings.some(w => w.severity === 'error') ? 'text-red-400' : 'text-amber-400'}`}>
                      {warnings.length} Allocation Warning{warnings.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="px-3 py-2 space-y-1.5">
                    {warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className={`mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full ${w.severity === 'error' ? 'bg-red-400' : 'bg-amber-400'}`} />
                        <span className="text-muted-foreground">{w.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Editable fields */}
              <div className="space-y-4">
                {/* Description */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Job description..."
                    rows={2}
                    disabled={isLocked}
                    data-testid="job-edit-description"
                  />
                </div>

                {/* Date Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Start Date <span className="text-red-500">*</span></Label>
                    <DatePicker
                      value={form.start_date}
                      onChange={(date) => setForm({ ...form, start_date: date })}
                      formatDisplay={formatDateDisplay}
                      className="w-full"
                      disabled={isLocked}
                      testId="job-edit-start-date"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">End Date</Label>
                    <DatePicker
                      value={form.end_date}
                      onChange={(date) => setForm({ ...form, end_date: date })}
                      formatDisplay={formatDateDisplay}
                      minDate={form.start_date}
                      className="w-full"
                      disabled={isLocked}
                      testId="job-edit-end-date"
                    />
                  </div>
                </div>

                {/* Time Row */}
                <div className="grid grid-cols-2 gap-3">
                  {tenantSettings?.depot_time_enabled && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Depot Time</Label>
                      <div className="flex gap-1.5">
                        <Select
                          value={form.depot_time?.split(':')[0] || ''}
                          onValueChange={(hour) => {
                            const min = form.depot_time?.split(':')[1] || '00';
                            setForm({ ...form, depot_time: `${hour}:${min}` });
                          }}
                          disabled={isLocked}
                        >
                          <SelectTrigger className="w-16 font-mono text-sm" data-testid="job-edit-depot-hour">
                            <SelectValue placeholder="HH" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(h => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="flex items-center font-bold">:</span>
                        <Select
                          value={form.depot_time?.split(':')[1] || ''}
                          onValueChange={(minute) => {
                            const hr = form.depot_time?.split(':')[0] || '06';
                            setForm({ ...form, depot_time: `${hr}:${minute}` });
                          }}
                          disabled={isLocked}
                        >
                          <SelectTrigger className="w-16 font-mono text-sm" data-testid="job-edit-depot-min">
                            <SelectValue placeholder="MM" />
                          </SelectTrigger>
                          <SelectContent>
                            {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Start Time <span className="text-red-500">*</span></Label>
                    <div className="flex gap-1.5">
                      <Select
                        value={form.start_time?.split(':')[0] || '07'}
                        onValueChange={(hour) => {
                          const min = form.start_time?.split(':')[1] || '00';
                          setForm({ ...form, start_time: `${hour}:${min}` });
                        }}
                        disabled={isLocked}
                      >
                        <SelectTrigger className="w-16 font-mono text-sm" data-testid="job-edit-start-hour">
                          <SelectValue placeholder="HH" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(h => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="flex items-center font-bold">:</span>
                      <Select
                        value={form.start_time?.split(':')[1] || '00'}
                        onValueChange={(minute) => {
                          const hr = form.start_time?.split(':')[0] || '07';
                          setForm({ ...form, start_time: `${hr}:${minute}` });
                        }}
                        disabled={isLocked}
                      >
                        <SelectTrigger className="w-16 font-mono text-sm" data-testid="job-edit-start-min">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent>
                          {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Duration + End Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Est. Duration</Label>
                    <Select
                      value={form.est_duration?.toString() || 'none'}
                      onValueChange={(v) => setForm({ ...form, est_duration: v === 'none' ? '' : parseFloat(v) })}
                      disabled={isLocked}
                    >
                      <SelectTrigger className="font-mono text-sm" data-testid="job-edit-duration">
                        <SelectValue placeholder="Select hours" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-</SelectItem>
                        {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12].map(h => (
                          <SelectItem key={h} value={h.toString()}>{h} {h === 1 ? 'hour' : 'hours'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {endTime && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Est. End Time</Label>
                      <div className="h-9 px-3 py-2 rounded-md border border-input bg-muted/50 font-mono text-sm flex items-center">
                        {endTime.time}
                        {endTime.nextDay && <span className="text-xs text-amber-600 font-medium ml-2">(+1 day)</span>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}
                    disabled={isLocked}
                  >
                    <SelectTrigger data-testid="job-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_allocated">Not Allocated</SelectItem>
                      <SelectItem value="allocated">Allocated</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* PO Number */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">PO Number</Label>
                  <Input
                    value={form.po_number}
                    onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                    placeholder="Purchase Order number"
                    disabled={isLocked}
                    data-testid="job-edit-po"
                  />
                </div>

                {/* Starting Location — gated on field_rules.starting_location.enabled */}
                {tenantSettings?.field_rules?.starting_location?.enabled && (
                  <div className="space-y-1.5" data-testid="starting-location-section">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Navigation className="h-3.5 w-3.5" />
                      Starting Location {tenantSettings?.field_rules?.starting_location?.required && <span className="text-red-500">*</span>}
                    </Label>
                    <GooglePlacesAutocomplete
                      placeholder="Where will workers start from?"
                      country={tenantSettings?.country}
                      value={form.starting_location || ''}
                      onChange={(e) => setForm({ ...form, starting_location: e.target.value })}
                      onPlaceSelect={(place) => setForm({ ...form, starting_location: place.formatted_address || place.street_address || '' })}
                      disabled={isLocked}
                      data-testid="job-starting-location-input"
                    />
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    disabled={isLocked}
                    data-testid="job-edit-notes"
                  />
                </div>

                {/* Night Shift & TBC */}
                <div className="flex items-center gap-6" data-testid="job-edit-flags">
                  <div className="flex items-center gap-2">
                    <Switch id="je-night-shift" checked={form.is_night_shift} onCheckedChange={v => setForm({ ...form, is_night_shift: v })} disabled={isLocked} data-testid="job-edit-night-shift" />
                    <Label htmlFor="je-night-shift" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Moon className="h-3.5 w-3.5 text-indigo-400" /> Night Shift
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="je-tbc" checked={form.is_tbc} onCheckedChange={v => setForm({ ...form, is_tbc: v })} disabled={isLocked} data-testid="job-edit-tbc" />
                    <Label htmlFor="je-tbc" className="cursor-pointer text-sm text-zinc-400">TBC</Label>
                  </div>
                </div>

                {/* Tags display (read-only in quick edit) */}
                {form.tags?.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tags</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {form.tags.map((tag, i) => (
                        <Badge key={typeof tag === "string" ? tag : i} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Footer */}
        {job && (
          <SheetFooter className="pt-3 border-t shrink-0 flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenFullEditor}
              className="gap-1.5"
              data-testid="job-edit-full-editor-btn"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Full Editor
            </Button>
            <div className="flex-1" />
            {!isLocked && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-1.5"
                data-testid="job-edit-save-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save Changes
              </Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
